import React from "react";
import { File, FileSpreadsheet, LoaderCircle } from "lucide-react";
import { MGMicrotext } from "../MGMicrotext";
import { MGTag } from "../MGTag";
import { TMGDocumentListItemProps } from "./MGDocumentList.types";
import styles from "./MGDocumentList.module.css";
import { MGLabel } from "../MGLabel";
import { MGColors } from "../../colors";
import externalLink from "../../../images/link-external.svg";
import iconCheck from "../../../images/icon-check.svg";

export const MGDocumentListItem = ({
  name,
  extension = "pdf",
  additionalInfo,
  appearance = "transparent",
  ariaLabelCard,
  className,
  style,
  dataTestId,
  tagProps,
  disabled = false,
  downloading = false,
  checked = false,
  onDownloadClick,
}: TMGDocumentListItemProps) => {
  const Icon = extension === "xls" ? FileSpreadsheet : File;
  const locked = disabled || downloading;
  const hasInfo = additionalInfo && Object.values(additionalInfo).some(Boolean);
  const dateAndSize = [additionalInfo?.size, additionalInfo?.date].filter(Boolean).join(" - ");

  const rightIcon = checked ? iconCheck : externalLink;

  return (
    <div
      className={`${styles.item} ${className || ""}`}
      style={style}
      data-appearance={appearance}
      data-disabled={disabled}
      data-testid={dataTestId}
      data-interactive={!!onDownloadClick && !locked}
      aria-busy={downloading || undefined}>
      <Icon
        className={styles.fileIcon}
        size={24}
        aria-hidden="true"
        data-testid={dataTestId && `${dataTestId}-icon-${extension}`}
      />
      <div className={styles.details}>
        <MGLabel
          tagName="span"
          labelStyleDefinition="80-05"
          className={styles.name}
          color="text-primary-01"
          title={name}>
          {name}
        </MGLabel>
        <span className={styles.srOnly}>{extension.toUpperCase()}</span>
        {hasInfo && (
          <MGMicrotext
            tagName="div"
            microtextStyleDefinition="microcopy-80-03"
            color="text-primary-03"
            className={styles.info}>
            {additionalInfo?.type && <span>{additionalInfo.type}</span>}
            {additionalInfo?.accountNumber && <span>{additionalInfo.accountNumber}</span>}
            {dateAndSize && <span>{dateAndSize}</span>}
          </MGMicrotext>
        )}
        {tagProps && (
          <MGTag
            {...tagProps}
            appearance={tagProps.appearance || "grey"}
            disabled={tagProps.disabled || disabled}
            className={`${styles.documentTag} ${tagProps.className || ""}`}
          />
        )}
      </div>
      {onDownloadClick && (
        <button
          type="button"
          className={styles.download}
          disabled={locked}
          onClick={onDownloadClick}
          aria-label={ariaLabelCard || `Transferir ${name} (${extension.toUpperCase()})`}
          title={downloading ? "A transferir..." : `Transferir ${name}`}
          data-testid={dataTestId && `${dataTestId}-download`}>
          {downloading ? (
            <LoaderCircle className={styles.spinner} size={24} aria-hidden="true" />
          ) : (
            <img
              alt=""
              src={rightIcon}
              width={24}
              height={24}
              aria-hidden="true"
              color={checked ? MGColors["feedback-success-80"].hex : MGColors["brand-01-100"].hex}
            />
          )}
        </button>
      )}

      {downloading && (
        <span className={styles.srOnly} role="status">
          A transferir {name}...
        </span>
      )}
    </div>
  );
};
